<template>
  <div>
    <b-modal
      :id="modalId"
      :size="modalSize"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-footer
      centered
      @show="getSubscriptionInformation"
      @hide="() => {}"
    >
      <template #modal-header>
        <div class="row w-100 no-gutters align-items-center">
          <div class="col">
            <h2 class="modal-title" data-test-hook="new-subscription-modal-header">
              <i class="far fa-badge-dollar colorOrange mr-1" data-test-hook="new-subscription-modal-header-icon" />
              {{ modalTitle }}
            </h2>
          </div>
          <div class="float-right col-auto">
            <button
              type="button"
              class="btn btn-icon-button btn-icon-button--gray"
              @click="closeMainModal()"
              data-test-hook="CloseModalBtn"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </div>
      </template>
      <div v-if="isLoadingSubscriptionData">
        <b-row class="text-center">
          <b-col>
            <b-spinner style="width: 10rem; height: 10rem" variant="warning" label="Spinning"></b-spinner>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <AddApparatusUpgradeSubscription
          v-if="activeView === subscriptionViews.UPGRADE_SINGLE_MULTI"
          :isEnrollment="false"
          :currentSubscription="existingSubscription"
          :newSubscription="selectedProduct"
          :apparatusList="apparatusList"
          :new-apparatuses="apparatusesToAddToPlan"
          :isCreatingPurchaseIntent="isCreatingPurchaseIntent"
          @openPayment="proceedToPayment"
          @closeModal="closeMainModal"
        />
        <UpgradeBasicToMulti
          v-if="activeView === subscriptionViews.UPGRADE_BASIC_MULTI"
          :new-subscription="productList"
          :current-subscription="[]"
          :new-apparatuses="apparatusList"
          :apparatus-list="apparatusList"
          @openPayment="proceedToPayment"
          @setSelectedProduct="setSelectedProduct"
        />
        <UpdateApparatusesExistingSub
          v-if="activeView === subscriptionViews.EXISTING_SUB"
          :current-subscription="existingSubscription"
          :apparatus-list="apparatusList"
          @updateExistingProductApparatuses="updateExistingProductApparatuses"
          @closeModal="closeMainModal"
        />
        <UpgradeFromLegacy
          v-else-if="activeView === subscriptionViews.UPGRADE_LEGACY"
          :accountData="accountProfile"
          :legacySubscriptions="activeLegacySubscriptions"
          :selectedProduct="selectedProduct"
          :annualProduct="productList"
          :apparatusList="apparatusList"
          @setSelectedProduct="setSelectedProduct"
          @openPayment="proceedToPayment"
        />
        <SubscriptionInfoError v-else-if="activeView === subscriptionViews.ERROR" @retry="getSubscriptionInformation" />
      </div>
    </b-modal>
    <PaymentModal
      :modal-id="`${modalId}-payment`"
      :selected-product="selectedProduct"
      :account-profile="accountProfile"
      @submit="payForProduct"
      @goBack="returnToSubscriptions"
      @savePremiumFeaturesGettingReadyInfo="handleUpgradeOrUpdateFlowUserFeedback"
    />

    <Modal modalId="update-apparatuses-spinner-modal" size="modal-lg">
      <ModalBody style="padding-top: 100px; padding-bottom: 100px">
        <div class="d-flex justify-content-center align-items-center">
          <b-spinner style="width: 10rem; height: 10rem" variant="warning"></b-spinner>
        </div>
      </ModalBody>
    </Modal>
  </div>
</template>

<script>
import * as Constants from '@/constants'
import UpgradeBasicToMulti from './UpgradeBasicToMulti.vue'
import UpdateApparatusesExistingSub from './UpdateApparatusesExistingSub.vue'
import PaymentModal from '../../Enrollment/Subscription/PaymentModal.vue'
import SubscriptionInfoError from '../../Enrollment/Subscription/SubscriptionInfoError.vue'
import { isFuelMonitorV2 } from '../../../utils/apparatus'
import UpgradeFromLegacy from '../../Enrollment/Subscription/UpgradeFromLegacy.vue'
import AddApparatusUpgradeSubscription from './AddApparatusUpgradeSubscription.vue'
import {
  isDealerManaged,
  subscriptionActive,
  subscriptionDetailContainsApparatus,
  subscriptionDetailActive,
  openModal,
  closeModal,
  pollForSubmissionUpdate,
  pendingChangeIsPendingButAcknowledged,
  pendingChangeIsAlreadyActive
} from '../../../utils/mixins'

const subscriptionViews = {
  NEW_HSB: 'subscription-hsb-new',
  NEW_TANK: 'subscription-tank-new',
  EXISTING_SUB: 'subscription-existing',
  NEW_TANK_WITH_BASIC: 'subscription-tank-basic-new',
  UPGRADE_SINGLE_MULTI: 'subscription-single-to-multi',
  UPGRADE_BASIC_MULTI: 'subscription-basic-to-multi',
  ERROR: 'subscription-error',
  UPGRADE_LEGACY: 'subscription-legacy-upgrade'
}

/**
 * Modal shown from the SubscriptionSettings details page when the Account Needs Attention banner is clicked on and the user
 * needs to upgrade to a higher plan, or update their existing plan to incorporate an apparatus not currently on a plan but they have room for.
 */
export default {
  props: [
    'modalId',
    'apparatusId',
    /** All apparatuses (full apparatus objects, not just apparatusIds) that need to be on the updated/upgraded subscription. */
    'apparatusList',
    'accountProfile'
  ],
  inject: ['updateSubscriptionDetails'],
  components: {
    PaymentModal,
    SubscriptionInfoError,
    UpgradeBasicToMulti,
    UpdateApparatusesExistingSub,
    UpgradeFromLegacy,
    AddApparatusUpgradeSubscription
  },
  data() {
    return {
      activeView: null,
      productList: [],
      basicHsbList: null,
      existingSubscription: null,
      activeLegacySubscriptions: [],
      getSubscriptionInfoErrorMessage: null,
      isLoadingSubscriptionData: true,
      selectedProduct: null,
      showGetSubscriptionInfoError: false,
      subscriptionViews,
      isCreatingPurchaseIntent: false,
      apparatusesToAddToPlan: []
    }
  },
  watch: {
    apparatusId: function(newVal, oldVal) {
      if (!oldVal && newVal) {
        this.getSubscriptionInformation()
      }
    }
  },
  methods: {
    /** Validation function for use with global pollForSubmissionUpdate function for upgrade/update subscription detection. Also returns a subscription if it finds one that matches the validation criteria.*/
    updateUpgradePollingValidator(newSubscriptionData) {
      const nonDmApparatuses = this.apparatusList.filter(apparatus => !apparatus.isDealerManaged)

      return newSubscriptionData.subscriptions?.find(
        sub =>
          // Sub must be active, pending change, or pending cancel.
          subscriptionDetailActive(sub.status) &&
          // A user cannot upgrade or update a DM plan. DM plans are managed in Fleet. A DM plan is for a singular apparatus (i.e., tied to only one HSB or FM).
          !isDealerManaged(sub?.product?.productId) &&
          sub.apparatuses?.length > 0 &&
          // Since they have to upgrade all apparatus, we only need to check that one of those apparatuses is included.
          subscriptionDetailContainsApparatus(sub.apparatuses, nonDmApparatuses[0].apparatusId) &&
          (pendingChangeIsPendingButAcknowledged(sub, this.selectedProduct.productId) ||
            pendingChangeIsAlreadyActive(sub, this.selectedProduct.productId))
      )
    },
    /**
     * Handles post-payment subscription upgrade/update flows.
     * Called after the user successfully pays for a new subscription product via PaymentModal.
     *
     * Flow:
     * 1. Polls backend for subscription updates using pollForSubmissionUpdate with a validator
     * 2. Validator checks if any active subscription contains the first apparatus (polls until found)
     * 3. Once polling completes, either:
     *    a. Finds a match:
     *       Then: Close spinner, update subscription details, show green success toast,
     *    b. Does not find a match, and the subscription upgrade/update is still processing:
     *       - Adds pending subscription change info to global state to alert Dashboard/SettingsSubscriptions to display the blue banner
     * @async
     * @returns {Promise<void>}
     */
    async handleUpgradeOrUpdateFlowUserFeedback() {
      // Poll for subscription details until the conditions of the validator are met (i.e., we find a subscription that includes the updates we expect).
      this.pollForSubmissionUpdate(
        'getSubscriptionDetails',
        this.accountProfile.organizationId,
        this.updateUpgradePollingValidator,
        'lg'
      )
        .then(newSubscriptionData => {
          const newSubscriptionDetails = this.updateUpgradePollingValidator(newSubscriptionData)
          // Show the green toast if the API has acknowledged our change (either immediately applied or pending).
          if (newSubscriptionDetails) {
            // Clear any existing pending changes because an update or upgrade will overwrite anything pending.
            this.$store.dispatch('deletePendingSubscriptionChanges')

            this.closeLocalSpinnerModal()
            this.updateSubscriptionDetails(newSubscriptionData)
            this.$store.dispatch('displayToast', {
              title: this.$t('xSuccess'),
              message: this.$t('xPlanChangedSuccessfully'),
              variant: 'success'
            })
          }
          // Otherwise, processing is not complete yet, show the blue banners.
          else {
            const pendingChange = {
              apparatusIds: [...this.apparatusList.map(apparatus => apparatus.apparatusId)],
              pendingProductId: this.selectedProduct.productId,
              isPendingCancel: false
            }
            this.$store.dispatch('addPendingSubscriptionChanges', pendingChange)
          }
        })
        .catch(() => {
          this.showErrorToast()
        })
    },
    /** This returns a list of eligible subscriptions and sets selectedProduct to the yearly version of the subscription */
    async getSubscriptionProduct() {
      const apparatusTypeToAddNonUnique = this.apparatusList
        .map(apparatus => Constants.ApparatusTypesAsString[apparatus.type])
        .join(',')
      // trim list to unique values
      const apparatusTypeToAdd = [...new Set(apparatusTypeToAddNonUnique.split(','))].join(',')
      var annualPaidProduct = null
      if (apparatusTypeToAdd) {
        await this.$store
          .dispatch('getSubscriptionProductsByStoreOrganization', {
            organizationId: this.accountProfile.organizationId
          })
          .then(getProductsResponse => {
            annualPaidProduct = this.getEligibleSubscription(getProductsResponse.data.products)
            if (!annualPaidProduct) {
              throw new Error()
            }
            this.selectedProduct = annualPaidProduct[1] //default to first yearly product
          })
          .catch(error => {
            // Do nothing for now.
          })
      }

      return annualPaidProduct
    },

    requireUpgrade() {
      if (!this.existingSubscription) {
        return true
      }

      const existingHSBCount = this.apparatusList.reduce((sum, app) => {
        if (app.type === Constants.HsbApparatusType) {
          return sum + 1
        }

        return sum
      }, 0)
      const existingTankCount = this.apparatusList.reduce((sum, app) => {
        if (app.type === Constants.FuelTankApparatusType) {
          return sum + 1
        }

        return sum
      }, 0)

      const appHSBString = Constants.ApparatusTypeToString[Constants.HsbApparatusType]
      const appTankString = Constants.ApparatusTypeToString[Constants.FuelTankApparatusType]

      const apparatusHSBLimit = this.existingSubscription.product?.apparatusTypesMax?.[appHSBString] ?? 0
      const apparatusTankLimit = this.existingSubscription.product?.apparatusTypesMax?.[appTankString] ?? 0

      return existingHSBCount > apparatusHSBLimit || existingTankCount > apparatusTankLimit
    },

    async getSubscriptionInformation() {
      // Reset state to prevent stale data from being displayed when modal reopens.
      this.isLoadingSubscriptionData = true
      this.selectedProduct = null

      this.productList = await this.getSubscriptionProduct()

      const getExistingSubscriptionsResponse = await this.$store.dispatch(
        'getSubscriptionDetails',
        this.accountProfile.organizationId
      )
      if (getExistingSubscriptionsResponse?.data?.subscriptions) {
        this.existingSubscription = getExistingSubscriptionsResponse?.data?.subscriptions.find(
          subscription =>
            subscription.store === Constants.WebSubscriptionStore &&
            subscription.status !== Constants.SubscriptionDetailCanceled
        )
        this.activeLegacySubscriptions = this.getActiveLegacySubscriptions(getExistingSubscriptionsResponse.data)
      }

      /** If the plan requires an upgrade and the existing subscription is monthly, need to update selectedProduct */
      if (this.requireUpgrade()) {
        if (this.existingSubscription?.product?.durationInMonths === 1 && this.productList.length > 0) {
          this.selectedProduct = this.productList[0] // Get monthly option
        }
      }

      if (this.existingSubscription && this.apparatusList) {
        /** Checks for non DM, non V1 tanks, and not within an existing subscription */
        this.apparatusesToAddToPlan = this.apparatusList.filter(
          apparatus =>
            !apparatus.isDealerManaged &&
            apparatus.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value?.deviceType !==
              Constants.TankUtilityLteDeviceTypeV1 &&
            apparatus.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value?.deviceType !==
              Constants.TankUtilityWifiDeviceType &&
            !this.existingSubscription?.apparatuses.some(x => x.apparatusId === apparatus.apparatusId)
        )
      }

      // They have a subscription and have room to add to it.
      if (this.existingSubscription && !this.requireUpgrade()) {
        this.activeView = subscriptionViews.EXISTING_SUB
      }
      // They have a subscription, but an upgrade is required to add new apparatuses to it.
      else if (this.existingSubscription && this.requireUpgrade()) {
        this.activeView = subscriptionViews.UPGRADE_SINGLE_MULTI
      }
      // They have a legacy subscription.
      else {
        if (this.activeLegacySubscriptions?.length > 0) {
          this.activeView = subscriptionViews.UPGRADE_LEGACY
        }
        // They have no subscription at all
        else {
          this.activeView = subscriptionViews.UPGRADE_BASIC_MULTI
        }
      }
      this.isLoadingSubscriptionData = false

      return
    },

    proceedToPayment() {
      if (this.selectedProduct && this.selectedProduct.cost > 0) {
        this.isCreatingPurchaseIntent = true
        const apparatusIds = []
        this.apparatusList.forEach(apparatus => {
          apparatusIds.push(apparatus.apparatusId)
        })
        this.$store
          .dispatch('createPurchaseIntent', {
            productId: this.selectedProduct.productId,
            organizationId: this.accountProfile.organizationId,
            apparatusIds: apparatusIds
          })
          .then(() => {
            this.$bvModal.hide(this.modalId)
            this.$bvModal.show(`${this.modalId}-payment`)
          })
          .catch(() => {
            this.isCreatingPurchaseIntent = false
            this.showErrorToast()
          })
      } else if (this.selectedProduct && this.selectedProduct.cost === 0) {
        this.$bvModal.hide(this.modalId)
      }
    },

    returnToSubscriptions() {
      this.$bvModal.hide(`${this.modalId}-payment`)
      this.$bvModal.show(this.modalId)
    },

    setSelectedProduct(product) {
      this.selectedProduct = product
    },

    /** Shows red error toast. */
    showErrorToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xSomethingWentWrong'),
        message: this.$t('xPleaseWaitAndTryAgain'),
        variant: 'danger'
      })
    },

    payForProduct({ resolve, reject }) {
      if (this.accountProfile?.organizationId && this.selectedProduct?.productId && this.apparatusList) {
        // // note: when upgrading from SHP to MHP, we don't need to cancel anything - just create purchase intent and update sub product
        const apparatusIds = []
        this.apparatusList.forEach(apparatus => {
          apparatusIds.push(apparatus.apparatusId)
        })

        const changeSubscriptionMode = {
          organizationId: this.accountProfile.organizationId,
          productId: this.selectedProduct.productId
        }

        this.$store
          .dispatch('createPurchaseIntent', {
            productId: this.selectedProduct.productId,
            organizationId: this.accountProfile.organizationId,
            apparatusIds: apparatusIds
          })
          .then(() => {
            this.$store
              .dispatch('updateSubscriptionProduct', changeSubscriptionMode)
              .then(response => {
                this.isCreatingPurchaseIntent = false
                // send back success to the payment modal
                resolve(response)
                this.$bvModal.hide(`${this.modalId}-payment`)
              })
              .catch(error => {
                this.isCreatingPurchaseIntent = false
                let failureMessage = this.$t('xFailedSetUpGeneratorSubscription')
                if (error.response?.data?.message) {
                  try {
                    let messageAsJson = JSON.parse(error.response.data.message.replace(/\\"/g, '"'))
                    if (messageAsJson?.gatewayResponse?.message) {
                      failureMessage = messageAsJson.gatewayResponse.message
                    }
                  } catch (err) {}
                }
                // send error back to payment modal - show this error and do not proceed
                reject(failureMessage)
              })
          })
          .catch(error => {
            this.isCreatingPurchaseIntent = false
            // send error back to payment modal - show this error and do not proceed
            reject(this.$t('xFailedSetUpGeneratorSubscription')) // FIXME, I don't think "reject" is a function in scope here.
          })
      } else {
        reject(this.$t('xFailedSetUpGeneratorSubscription'))
      }
    },

    /**
     * Updates the list of apparatuses associated with an existing subscription.
     * This is the "update" flow where the user adds new devices to their current plan
     * without changing subscription tiers (as opposed to upgrading from Single to Multi Home).
     *
     * Triggered by the UpdateApparatusesExistingSub component when user confirms adding
     * apparatuses to their existing subscription that has available capacity.
     *
     * Flow:
     * 1. Closes the main modal and opens a spinner modal for visual feedback
     * 2. Calls updateSubscriptionApparatuses API to add apparatuses to existing subscription
     * 3. If subscription has a pending product change (status = PendingProductChange):
     *    - Creates purchase intent for the CURRENT product (not the pending one)
     *    - Updates subscription product to confirm current product selection
     *    - This effectively cancels the pending change and keeps current subscription
     * 4. After successful update:
     *    - Calls handleUpgradeOrUpdateFlowUserFeedback() to poll for completion and show user feedback (i.e., blue banner or green toast)
     * 5. On error:
     *    - Closes spinner and shows error toast
     * 6. Finally:
     *    - Ensures spinner modal is closed
     *    - Resets activeView to null
     *
     * Note: This function handles the special case where a user might have a pending
     * subscription change but wants to update their current plan instead. By creating
     * a purchase intent for the current product, we override the pending change.
     *
     * @async
     * @returns {Promise<void>}
     */
    async updateExistingProductApparatuses() {
      this.closeMainModal()
      this.openLocalSpinnerModal()

      const apparatusIds = []
      this.apparatusList.forEach(apparatus => {
        apparatusIds.push(apparatus.apparatusId)
      })

      await this.$store
        .dispatch('updateSubscriptionApparatuses', {
          organizationId: this.accountProfile.organizationId,
          store: this.existingSubscription?.store,
          productId: this.existingSubscription?.product?.productId,
          apparatusIds: apparatusIds
        })
        .then(() => {
          // If there is a pending change and we are trying to update the current subscription, override the pending change.
          if (this.existingSubscription.status === Constants.SubscriptionDetailPendingProductChange) {
            // Indicate that the currently active subscription product is the one we want. Cancel out the pending change.
            return this.$store
              .dispatch('createPurchaseIntent', {
                productId: this.existingSubscription.product.productId,
                organizationId: this.accountProfile.organizationId,
                apparatusIds: apparatusIds
              })
              .then(() => {
                return this.$store.dispatch('updateSubscriptionProduct', {
                  organizationId: this.accountProfile.organizationId,
                  productId: this.existingSubscription.product.productId
                })
              })
          }
        })
        .then(async () => {
          await this.$store
            .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
            .then(response => {
              this.updateSubscriptionDetails(response.data)
              this.$store.dispatch('displayToast', {
                title: this.$t('xSuccess'),
                message: this.$t('xPlanChangedSuccessfully'),
                variant: 'success'
              })
            })
            .catch(() => {
              this.showErrorToast()
            })
        })
        .catch(() => {
          this.showErrorToast()
        })
        .finally(() => {
          this.closeLocalSpinnerModal()
          this.activeView = null
        })
    },

    getEligibleSubscription(products) {
      if (!this.apparatusList) {
        return null
      }

      const apparatusTypeCounts = this.apparatusList?.reduce(
        (ctr, app) => {
          const newCtr = { ...ctr }
          if (app.isDealerManaged) {
            return newCtr // dealer managed don't count toward subscription eligibility
          }

          if (app.type === Constants.HsbApparatusType) {
            newCtr.hsb = ctr.hsb + 1
          } else if (app.type === Constants.FuelTankApparatusType) {
            // v1 tanks aren't added to subscriptions, nor do they prevent a user from having a single-home subscription
            // HOWEVER, they are included when determining if there are 6 apparatuses (the max) on the account, because of course they are
            if (isFuelMonitorV2(app)) {
              newCtr.tank = ctr.tank + 1
            } else {
              newCtr.tankV1 = ctr.tankV1 + 1
            }
          }

          return newCtr
        },
        {
          hsb: 0,
          tank: 0,
          tankV1: 0
        }
      )

      const apparatusCount = apparatusTypeCounts.hsb + apparatusTypeCounts.tank

      if (apparatusCount + apparatusTypeCounts.tankV1 > 6) {
        return null
      }

      return products.filter(product => {
        if (apparatusCount > product.maxApparatuses) {
          return false
        }

        return (
          apparatusTypeCounts.hsb <= (product.apparatusTypesMax.Generator ?? 0) &&
          apparatusTypeCounts.tank <= (product.apparatusTypesMax.FuelTank ?? 0)
        )
      })
    },
    getActiveLegacySubscriptions(subscriptionData) {
      if (!subscriptionData) {
        return []
      }

      return subscriptionData?.legacyBillingProducts?.products?.filter(
        product =>
          product.isLegacy && // is a legacy sub that...
          subscriptionActive(product) && // is active, but is NOT...
          !product.productId.includes('free-wifi') && // a free sub,...
          !(
            product.productId === Constants.DealerManagedProductId ||
            product.productId === Constants.TankUtilityDealerManagedProductId
          ) && // dealer managed, or...
          !this.apparatusList // a v1 tank
            ?.find(apparatus => apparatus.apparatusId === product.apparatusId)
            ?.properties.find(property => property.name === 'Device' && property.deviceType?.includes('tankutility-v1'))
      )
    },
    openModal,
    closeModal,
    pollForSubmissionUpdate,
    closeMainModal() {
      this.$bvModal.hide(this.modalId)
    },
    closeLocalSpinnerModal() {
      this.closeModal('update-apparatuses-spinner-modal')
    },
    openLocalSpinnerModal() {
      this.openModal('update-apparatuses-spinner-modal')
    }
  },
  computed: {
    modalSize() {
      switch (this.activeView) {
        case subscriptionViews.NEW_HSB:
        case subscriptionViews.UPGRADE_SINGLE_MULTI:
          return 'lg'
        default:
          return 'md'
      }
    },
    modalTitle() {
      switch (this.activeView) {
        case subscriptionViews.EXISTING_SUB:
          return this.$t('xCongratulations')
        case subscriptionViews.UPGRADE_SINGLE_MULTI:
        case subscriptionViews.UPGRADE_LEGACY:
          return this.$t('xUpgradeYourPlan')
        case subscriptionViews.UPGRADE_BASIC_MULTI:
          return this.$t('xUpgradePremiumMonitoring')
        default:
          return this.$t('xSubscribeToPremium')
      }
    }
  }
}
</script>
<style lang="scss">
.legal-text {
  font-size: 0.875em;
  color: var(--gray-600);
}
</style>
